<template>
  <van-tabs
    v-bind="tabsProps"
    class="tabs"
    :class="`${scene} ${size}`"
    :active="active"
    shrink
    @change="(name: string) => emit('update:active', name)"
    animated
    :type="'line'"
  >
    <template v-for="(it, index) in items">
      <van-tab v-if="$slots[getName(it)]" :name="getName(it)">
        <template #title>
          <div class="title">{{ it.title }}</div>
          <div v-if="divider && index < items.length - 1" class="divider">
            /
          </div>
        </template>
        <slot
          v-if="active === getName(it)"
          :item="it"
          :index="index"
          :name="getName(it)"
        >
          {{ it.text }}
        </slot>
      </van-tab>
      <van-tab v-else :title="it.title" :name="getName(it)">
        <slot :item="it" :index="index">
          {{ it.text }}
        </slot>
      </van-tab>
    </template>
  </van-tabs>
</template>

<script
  setup
  lang="ts"
  generic="T extends Partial<TabProps> & { text?: string } = any"
>
import { computed } from "vue";
import { Tabs as VanTabs, Tab as VanTab, TabProps } from "vant";

interface TabsProps {
  active?: number | string;
  size?: "normal" | "large";
  scene?: "plain" | "tab" | "pure";
  items: T[];
  namePropName?: string;
  divider?: boolean;
}

const props = withDefaults(defineProps<TabsProps>(), {
  active: 0,
  scene: "plain",
  size: "normal",
  items: () => [],
  namePropName: "name",
});

type Event = {
  (e: "update:active", val: number | string): void;
};

const emit = defineEmits<Event>();

const tabsProps = computed(() => {
  const { items, ...rest } = props;
  return rest;
});
const isLarge = () => {
  console.log("large ===>", window.innerWidth >= 1360);
  return window.innerWidth >= 1360;
};
const getName = (item: any) => {
  const { namePropName } = props;
  return item?.[namePropName];
};
</script>
<style scoped lang="less">
.tabs {
  &.pure {
    width: 100%;

    :deep(.van-tabs) {
      &__wrap {
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom-width: 1px;
        border-bottom-color: rgba(255, 255, 255, 0.02);
        border-bottom-style: solid;
        @media screen and (max-width: 1360px) {
          justify-content: flex-start;
          padding-top: 0.26rem;
        }
      }

      &__line {
        bottom: 0.16rem;
        opacity: 0.8;
        height: 0.04rem;
        background: linear-gradient(
          97.03deg,
          #ffffff 20.89%,
          #b5ffdf 62.65%,
          #9cedff 83.53%,
          #97b4ff 104.41%
        );
      }

      &__nav {
        background: transparent;

        &--line {
          overflow: visible;
          padding-bottom: 0.28rem;

          @media screen and (max-width: 1360px) {
            margin-left: 0.32rem;
          }
        }

        .van-tab {
          color: rgba(255, 255, 255, 0.5);
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 0.6rem;
          font-weight: 500;
          line-height: 0.8rem;
          padding: 0;

          @media screen and (max-width: 1360px) {
            font-weight: 600;
            font-size: 0.32rem;
            line-height: 0.4rem;
          }

          &--line {
            @media screen and (max-width: 1360px) {
              margin-right: 0.56rem;
            }
          }

          &__text {
            --tab-mid-gap: 1.06rem;

            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            // height: 100%;

            position: relative;

            .title {
              padding: 0 var(--tab-mid-gap);
              width: 100%;
              height: 100%;
              display: inline-block;
              // display: flex;
              // justify-content: center;
              // align-items: center;

              @media screen and (max-width: 1360px) {
                padding-left: 0;
                padding-right: 0;
              }
            }

            .divider {
              position: absolute;
              top: 0;
              height: 100%;
              right: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              color: rgba(255, 255, 255, 0.3);
              mix-blend-mode: normal;
              @media screen and (max-width: 1360px) {
                right: -25%;
              }
            }
          }

          &--active {
            .van-tab__text .title {
              color: transparent;
              background: linear-gradient(
                97.03deg,
                #ffffff 20.89%,
                #b5ffdf 62.65%,
                #9cedff 83.53%,
                #97b4ff 104.41%
              );
              background-clip: text;
              display: inline-block;
            }
          }
        }
      }

      &__content {
        width: 100%;
      }
    }
  }

  &.tab {
    :deep(.van-tabs) {
      &__wrap {
        display: flex;
        justify-content: flex-start;
      }

      &__nav {
        &--active {
          color: #fff;
          background: none;
        }
      }
    }
  }

  &.normal {
    :deep(.van-tabs) {
      &__wrap {
        height: 0.96rem;
      }

      &__nav {
        .van-tab {
          font-size: 0.4rem;
          font-weight: 400;
          line-height: 0.48rem;

          @media screen and (max-width: 1360px) {
            font-size: 0.32rem;
            font-weight: 500;
            line-height: 0.44rem;
          }

          &__text {
            --tab-mid-gap: 1.06rem;

            @media screen and (max-width: 1360px) {
              --tab-mid-gap: 0.56rem;
            }
          }
        }
      }
    }
  }

  &.large {
    :deep(.van-tabs) {
      &__wrap {
        height: 1.12rem;
      }

      &__nav {
        &--line {
          padding-bottom: 0.28rem;
        }
      }
    }
  }
}
</style>
